import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Backdrop,
    Grid,
    IconButton,
    Divider,
    Tooltip,
    Button
} from '@mui/material';
import { ExpandMore, Add as AddIcon, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import EditorActions from '@/components/EditorActions.tsx';
import { validateName, required, validUrl, noWhitespace } from '@core/utils/validation';
import { FormContainer } from '@/components';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import { AuthenticationProvider, CustomProperty, PropertyMappings } from '@/services';
import WcnButton from '@/components/shared/WcnButton.tsx';

interface AuthenticationProviderEditorProps {
    item: AuthenticationProvider;
    isSaving: boolean;
    onSave: (item: AuthenticationProvider) => void;
    onDelete: (item: AuthenticationProvider) => void;
    onCancel: () => void;
}

const AuthenticationProviderEditor: React.FC<AuthenticationProviderEditorProps> = ({
    item,
    isSaving,
    onSave,
    onDelete,
    onCancel
}) => {
    const { t } = useTranslation();
    const { notifyWarning, notifyError } = useNotifications();
    const [formData, setFormData] = useState<AuthenticationProvider>(item);
    const [expandedPanel, setExpandedPanel] = useState<string | false>('panel1');

    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = React.useState(true);

    const authenticationTypes = [
        { value: 'Basic', text: 'basic' },
        { value: 'Bearer', text: 'bearer' },
        { value: 'ApiKey', text: 'apikey' },
        { value: 'Custom', text: 'custom' }
    ];

    const availableProperties = [
        { value: 'code', text: 'code' },
        { value: 'store', text: 'store' },
        { value: 'source', text: 'source' },
        { value: 'type', text: 'type' },
        { value: 'customProperties', text: 'customProperties' }
    ];

    const jsonPlaceholder = '{\n\t"key": "value"\n}';

    useEffect(() => {
        const updatedItem = { ...item };
        if (!updatedItem.customProperties) {
            updatedItem.customProperties = [];
        }
        setFormData(updatedItem);
    }, [item]);

    useEffect(() => {
        if (formData.authenticationType && formData.authenticationType.toLowerCase() !== 'custom') {
            setFormData((prev) => ({
                ...prev,
                authorizationHeader: 'Authorization'
            }));
        }
    }, [formData.authenticationType]);

    const isJsonValid = (json: string): boolean => {
        if (!json) return false;
        try {
            JSON.parse(json);
            return true;
        } catch (e) {
            return false;
        }
    };

    const jsonRule = (value: string): string => {
        if (!value) return '';
        try {
            JSON.parse(value);
            return '';
        } catch (e) {
            return `Invalid JSON format: ${(e as Error).message}`;
        }
    };

    const uniqueDeviceRule = (value: string, idx: number): string => {
        if (!value) return '';
        const duplicates =
            formData.customProperties?.filter((entry, index) => entry.deviceId === value && index !== idx) || [];
        return duplicates.length === 0 ? '' : t('deviceIdAlreadySelected');
    };

    const validatePropertyRule = (value: string, idx: number): string => {
        if (!value || value.trim() === '') return t('validation.required');

        const duplicates =
            formData.propertyMappings?.filter((entry, index) => entry.propertyName === value && index !== idx) || [];
        return duplicates.length === 0 ? '' : t('propertyNameAlreadySelected');
    };

    const handleChange = (field: keyof AuthenticationProvider, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCustomPropertyChange = (index: number, field: keyof CustomProperty, value: string) => {
        const updatedProperties = [...(formData.customProperties || [])];
        updatedProperties[index] = { ...updatedProperties[index], [field]: value };
        setFormData((prev) => ({ ...prev, customProperties: updatedProperties }));
    };

    const handlePropertyMappingChange = (index: number, field: keyof PropertyMappings, value: string) => {
        const updatedProperties = [...(formData.propertyMappings || [])];
        updatedProperties[index] = { ...updatedProperties[index], [field]: value };
        setFormData((prev) => ({ ...prev, propertyMappings: updatedProperties }));
    };

    const addCustomProperty = () => {
        const newProperty: CustomProperty = {
            deviceId: null,
            customPropertiesJson: ''
        };
        setFormData((prev) => ({
            ...prev,
            customProperties: [...(prev.customProperties || []), newProperty]
        }));
    };

    const addPropertyMapping = () => {
        const newMapping: PropertyMappings = {
            propertyName: null,
            mappingName: ''
        };
        setFormData((prev) => ({
            ...prev,
            propertyMappings: [...(prev.propertyMappings || []), newMapping]
        }));
    };

    const removeCustomProperty = (index: number) => {
        const updatedProperties = [...(formData.customProperties || [])];
        updatedProperties.splice(index, 1);
        setFormData((prev) => ({ ...prev, customProperties: updatedProperties }));
    };

    const removePropertyMapping = (index: number) => {
        const updatedProperties = [...(formData.propertyMappings || [])];
        updatedProperties.splice(index, 1);
        setFormData((prev) => ({ ...prev, propertyMappings: updatedProperties }));
    };

    const formatJson = (index: number) => {
        try {
            const entry = formData.customProperties?.[index];
            if (entry) {
                const parsed = JSON.parse(entry.customPropertiesJson);
                const formatted = JSON.stringify(parsed, null, 2);
                handleCustomPropertyChange(index, 'customPropertiesJson', formatted);
            }
        } catch (e) {
            notifyError('invalidJsonFormat');
        }
    };

    const handleSave = () => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (isValid) {
            onSave(formData);
        } else {
            notifyWarning('invalidInput');
        }
    };

    const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, idx: number) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const textarea = e.target as HTMLTextAreaElement;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const indent = '  ';

            const currentValue = formData.customProperties?.[idx]?.customPropertiesJson || '';
            const newValue = currentValue.substring(0, start) + indent + currentValue.substring(end);

            handleCustomPropertyChange(idx, 'customPropertiesJson', newValue);

            setTimeout(() => {
                textarea.selectionStart = start + indent.length;
                textarea.selectionEnd = start + indent.length;
            }, 0);
        }
    };

    const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const isCustomType = formData.authenticationType?.toLowerCase() === 'custom';

    if (isSaving) {
        return (
            <Box className="editor-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="editor-container">
            {/* Header Actions */}
            <Box className="editor-sidebar-actions">
                <EditorActions
                    onSave={handleSave}
                    onCancel={onCancel}
                    onDelete={() => onDelete(formData)}
                    saveDisabled={!formValid || isSaving}
                    showDelete={!!formData.id}
                />
            </Box>

            {/* Content */}
            <Box className="editor-sidebar-content authentication-provider-content">
                <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                    <Accordion expanded={expandedPanel === 'panel1'} onChange={handleAccordionChange('panel1')}>
                        <AccordionSummary expandIcon={<ExpandMore />} className="accordion-summary">
                            <Typography variant="h6" className="accordion-header">
                                <Box component="i" className="zmdi zmdi-view-list" />
                                {t('authenticationType')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-content">
                            <Box className="editor-form-container authentication-provider-container">
                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="name"
                                            label={t('name')}
                                            value={formData.name || ''}
                                            onChange={(e) => handleChange('name', e.target.value)}
                                            required
                                            fullWidth
                                            rules={[validateName(t)]}
                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormSelect
                                            name="authenticationType"
                                            label={t('type')}
                                            value={formData.authenticationType || ''}
                                            onChange={(e) => handleChange('authenticationType', e)}
                                            items={authenticationTypes.map((type) => ({
                                                value: type.value,
                                                label: t(type.text)
                                            }))}
                                            placeholder={t('selectAType')}
                                            itemText="label"
                                            itemValue="value"
                                            rules={[required(t)]}
                                            sx={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="authorizationHeader"
                                            label={t('authorizationHeader')}
                                            value={formData.authorizationHeader || ''}
                                            onChange={(e) => handleChange('authorizationHeader', e.target.value)}
                                            required
                                            fullWidth
                                            disabled={!isCustomType}
                                            rules={[required(t)]}
                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="url"
                                            label={t('url')}
                                            value={formData.url || ''}
                                            onChange={(e) => handleChange('url', e.target.value)}
                                            required
                                            fullWidth
                                            rules={[validUrl(t)]}
                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-col form-col-12 form-col-6">
                                        <FormTextField
                                            name="token"
                                            label={t('token')}
                                            value={formData.token || ''}
                                            onChange={(e) => handleChange('token', e.target.value)}
                                            required
                                            fullWidth
                                            rules={[required(t), noWhitespace(t)]}
                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                        />
                                    </div>
                                </div>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={expandedPanel === 'panel2'} onChange={handleAccordionChange('panel2')}>
                        <AccordionSummary expandIcon={<ExpandMore />} className="accordion-summary">
                            <Typography variant="h6" className="accordion-header">
                                <Box component="i" className="zmdi zmdi-view-list" />
                                {t('customPropsJson')}
                                <Tooltip title={t('customPropsJsonTooltip')} placement="right">
                                    <Box component="i" className="zmdi zmdi-help-outline help-icon-tooltip" />
                                </Tooltip>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-content">
                            <Box className="editor-form-container custom-props-section">
                                {formData.customProperties?.map((entry, idx) => (
                                    <Box key={idx} sx={{ marginTop: '-4px' }}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item xs={12} md={3}>
                                                <FormTextField
                                                    name={`customProperties.${idx}.deviceId`}
                                                    label={t('scannerDeviceId')}
                                                    value={entry.deviceId || ''}
                                                    variant="outlined"
                                                    onChange={(e) =>
                                                        handleCustomPropertyChange(idx, 'deviceId', e.target.value)
                                                    }
                                                    required
                                                    fullWidth
                                                    rules={[required(t), (value) => uniqueDeviceRule(value, idx)]}
                                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                />
                                                <WcnButton
                                                    variant="primary"
                                                    className="format-json-btn"
                                                    disabled={!isJsonValid(entry.customPropertiesJson)}
                                                    onClick={() => formatJson(idx)}
                                                >
                                                    {t('formatJson')}
                                                </WcnButton>
                                            </Grid>
                                            <Grid item xs={12} md={8}>
                                                <FormTextField
                                                    name={`customProperties.${idx}.customPropertiesJson`}
                                                    label={t('customPropsJson')}
                                                    variant="outlined"
                                                    value={entry.customPropertiesJson || ''}
                                                    onChange={(e) =>
                                                        handleCustomPropertyChange(
                                                            idx,
                                                            'customPropertiesJson',
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                    multiline
                                                    rows={6}
                                                    fullWidth
                                                    placeholder={jsonPlaceholder}
                                                    onKeyDown={(e) => handleTextareaKeyDown(e, idx)}
                                                    rules={[required(t), jsonRule]}
                                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={1}>
                                                <IconButton
                                                    color="error"
                                                    title="Remove"
                                                    onClick={() => removeCustomProperty(idx)}
                                                    className="remove-btn"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                        {idx < (formData.customProperties?.length || 0) - 1 && <Divider />}
                                    </Box>
                                ))}

                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    onClick={addCustomProperty}
                                    className="add-property-btn"
                                >
                                    <Box component="span" sx={{ display: { xs: 'none', md: 'flex' } }}>
                                        {t('add')}
                                    </Box>
                                </Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion expanded={expandedPanel === 'panel3'} onChange={handleAccordionChange('panel3')}>
                        <AccordionSummary expandIcon={<ExpandMore />} className="accordion-summary">
                            <Typography variant="h6" className="accordion-header">
                                <Box component="i" className="zmdi zmdi-view-list" />
                                {t('propertyMapping')}
                                <Tooltip title={t('propertyMappingTooltip')} placement="right">
                                    <Box component="i" className="zmdi zmdi-help-outline help-icon-tooltip" />
                                </Tooltip>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-content">
                            <Box className="editor-form-container custom-props-section">
                                {formData.propertyMappings?.map((entry, idx) => (
                                    <Box key={idx} sx={{ marginTop: '-4px' }}>
                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item xs={6} sm={6} md={6} lg={6}>
                                                <FormSelect
                                                    name={`property-${idx}`}
                                                    label={t('propertyName')}
                                                    value={entry.propertyName || ''}
                                                    onChange={(value) =>
                                                        handlePropertyMappingChange(idx, 'propertyName', value)
                                                    }
                                                    items={availableProperties.map((property) => ({
                                                        value: property.value,
                                                        label: property.text
                                                    }))}
                                                    itemText="label"
                                                    itemValue="value"
                                                    rules={[(value) => validatePropertyRule(value, idx)]}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={6} md={6} lg={6}>
                                                <FormTextField
                                                    name={'mappingName'}
                                                    label={t('mappingName')}
                                                    value={entry.mappingName || ''}
                                                    onChange={(value) =>
                                                        handlePropertyMappingChange(
                                                            idx,
                                                            'mappingName',
                                                            value.target.value
                                                        )
                                                    }
                                                    fullWidth
                                                    rules={[required(t)]}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={1}>
                                                <IconButton
                                                    color="error"
                                                    title="Remove"
                                                    onClick={() => removePropertyMapping(idx)}
                                                    className="remove-btn"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                        {idx < (formData.propertyMappings?.length || 0) - 1 && <Divider />}
                                    </Box>
                                ))}

                                <Button
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    onClick={addPropertyMapping}
                                    className="add-property-btn"
                                >
                                    <Box component="span" sx={{ display: { xs: 'none', md: 'flex' } }}>
                                        {t('add')}
                                    </Box>
                                </Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </FormContainer>
            </Box>

            {isSaving && (
                <Backdrop open={isSaving} className="editor-backdrop">
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </Box>
    );
};

export default AuthenticationProviderEditor;
